import { createFileRoute, Link, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin")({
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const { data: sess } = await supabase.auth.getSession();
    const uid = sess.session?.user.id;
    if (!uid) throw redirect({ to: "/login" });
    const { data } = await supabase
      .from("profiles")
      .select("is_platform_admin")
      .eq("id", uid)
      .maybeSingle();
    if (!data?.is_platform_admin) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: AdminLayout,
});

const tabs = [
  { to: "/admin/overview", label: "Overview" },
  { to: "/admin/accounts", label: "Accounts" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/patterns", label: "Patterns" },
  { to: "/admin/proposals", label: "Proposals" },
  { to: "/admin/notifications", label: "Notifications" },
  { to: "/admin/impersonation", label: "Impersonation" },
  { to: "/admin/ai-usage", label: "AI Usage" },
  { to: "/admin/audit", label: "Audit Log" },
  { to: "/admin/support", label: "Support" },
  { to: "/admin/settings", label: "Settings" },
] as const;

function AdminLayout() {
  const path = useRouterState({ select: (r) => r.location.pathname });
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Platform Admin
        </div>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">Admin Panel</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cross-tenant operations. Visible only to platform admins.
        </p>
      </div>
      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => {
          const active = path === t.to || path.startsWith(t.to + "/");
          return (
            <Link
              key={t.to}
              to={t.to}
              className={cn(
                "border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px",
                active
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
            </Link>
          );
        })}
      </div>
      <Outlet />
    </div>
  );
}
